import { GridHookForm, Typogaphy } from 'vnetwork-ui'
import TimeIcons from '@/icons/TimeIcons'
import StatementIcons from '@/icons/StatementIcons'
import {
    calculateServiceTimes,
    calculateStatementDates,
    defaultFirstEnddate,
} from '@/modules/Contract/constants/function'
import { Contract, ServiceConfigOption } from '@/modules/Contract/types'
import { Dayjs } from 'dayjs'
import { useEffect, useMemo, useRef } from 'react'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toDayjs } from '@/utils/function'

// Stable key for a form date, so effects compare calendar days rather than
// Dayjs object identity (which changes on every render). Empty input is mapped
// to '' rather than being handed to toDayjs, because dayjs(undefined) resolves
// to *now* and would make a cleared field look like today's date.
const dateValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return ''
    const parsed = toDayjs(value)
    return parsed?.isValid() ? parsed.format('YYYY-MM-DD') : ''
}

interface ServiceTimesProps {
    index: number
    disabled?: boolean
    disabledStatement?: boolean
    rootName?: string
    isAddon?: boolean
    // Set by templates that drive first_startdate/first_enddate from the
    // contract's own effective/expiry dates (domain, SSL — yearly terms).
    // Those must not have their end date re-derived on a monthly rule.
    datesFromContract?: boolean
}

const ServiceTimes: React.FC<ServiceTimesProps> = ({
    index,
    disabled = false,
    disabledStatement,
    rootName = `order.products.[${index}].service_times`,
    isAddon = false,
    datesFromContract = false,
}) => {
    const { t } = useTranslation()
    const formControl = useFormContext<Contract>()

    const statement = useWatch({
        control: formControl.control,
        name: `${rootName}.statement` as any,
    }) as string

    const productGroupName = useWatch({
        control: formControl.control,
        name: `order.products.[${index}].product_group_name` as any,
    }) as string

    const service_configoptions = useWatch({
        control: formControl.control,
        name: `order.products.[${index}].service_configoptions` as any,
    }) as ServiceConfigOption[]

    const first_enddate = useWatch({
        control: formControl.control,
        name: `${rootName}.first_enddate` as Path<Contract>,
    }) as Dayjs

    const first_startdate = useWatch({
        control: formControl.control,
        name: `${rootName}.first_startdate` as Path<Contract>,
    }) as Dayjs

    const list_commitment_time = service_configoptions?.map(
        (item) => item?.commitment_time
    )

    useEffect(() => {
        if (disabledStatement) return

        const statementTime = calculateStatementDates(
            toDayjs(first_startdate) as Dayjs,
            statement
        )

        formControl.setValue(
            `${rootName}.statement_startdate` as Path<Contract>,
            statementTime?.statement_startdate
        )
        formControl.setValue(
            `${rootName}.statement_enddate` as Path<Contract>,
            statementTime?.statement_enddate
        )
        // first_startdate belongs in the dependency list: without it the
        // statement window stays pinned to whichever month was current when the
        // form mounted, so moving the service into a later month left the
        // statement period on the old one and ProcessStatement billed it.
    }, [statement, dateValue(first_startdate)])

    const commitmentMonths = useMemo(() => {
        const values = (list_commitment_time ?? [])
            .map(Number)
            .filter((n) => Number.isFinite(n) && n > 1)
        return values.length ? Math.max(...values) : 1
    }, [JSON.stringify(list_commitment_time)])

    // Keep the first period's end anchored to its start and to the commitment
    // length. This replaces two separate effects that both wrote first_enddate
    // from `today` — they raced each other, and both left the end date stale
    // when the service was scheduled to start in a different month, which is
    // what forced the manual edit that produced a one-day period.
    //
    // Skipped on the first run so a form being hydrated (edit, tour, restored
    // draft) keeps its stored end date; only a genuine change re-derives it.
    const derivedFrom = useRef<string | null>(null)
    useEffect(() => {
        const key = `${dateValue(first_startdate)}|${commitmentMonths}`
        const isFirstRun = derivedFrom.current === null
        derivedFrom.current = key

        if (isFirstRun || datesFromContract || isAddon || disabled) return
        if (!dateValue(first_startdate)) return

        formControl.setValue(
            `${rootName}.first_enddate` as Path<Contract>,
            defaultFirstEnddate(toDayjs(first_startdate), commitmentMonths)
        )
    }, [dateValue(first_startdate), commitmentMonths])

    useEffect(() => {
        // if (!first_enddate) return
        const serviceTimes = calculateServiceTimes(
            toDayjs(first_startdate),
            toDayjs(first_enddate)
        )

        formControl.setValue(
            `${rootName}.continue_startdate` as Path<Contract>,
            toDayjs(serviceTimes.continue_startdate)
        )
        formControl.setValue(
            `${rootName}.continue_enddate` as Path<Contract>,
            toDayjs(serviceTimes.continue_enddate)
        )
    }, [JSON.stringify([first_enddate, first_startdate])])

    return (
        <div
            className="bg-white rounded border border-slate-200 hover:border-slate-300 transition-colors duration-200"
            data-tour="service-times"
        >
            {/* Header */}
            <div className="px-3 py-2.5 bg-purple-50/50 border-b border-purple-200/60">
                <div className="flex items-center gap-2.5">
                    <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center flex-shrink-0">
                        <TimeIcons className="w-3.5 h-3.5 text-purple-600" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <Typogaphy className="font-medium text-purple-800 text-sm">
                                {t('service-time')}
                            </Typogaphy>
                            <span className="text-xs text-purple-600">•</span>
                            <Typogaphy
                                size="small"
                                className="text-purple-600 text-xs"
                            >
                                {t('service_time_configuration')}
                            </Typogaphy>
                        </div>
                    </div>
                </div>
            </div>

            {/* Service Periods */}
            <div className="px-3 py-2.5">
                {/* First Period */}
                <div className="border border-blue-200/60 rounded-t-md">
                    <div className="px-3 py-2 bg-blue-50/50 border-blue-200/60">
                        <div className="flex items-center gap-2.5">
                            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-blue-600 font-medium text-xs">
                                    1
                                </span>
                            </div>
                            <Typogaphy className="font-medium text-blue-800 text-sm">
                                {t('services.first_period')}
                            </Typogaphy>
                        </div>
                    </div>
                </div>
                <GridHookForm
                    subForm
                    formControl={formControl}
                    actions={[]}
                    className="grid-cols-2 mt-2"
                    data={[
                        {
                            formItemProps: {
                                name: `${rootName}.first_startdate` as any,
                                type: 'datepicker',
                                placeholder: t('first_startdate'),
                                disabled,
                                size: 'small',
                            },
                        },
                        {
                            formItemProps: {
                                name: `${rootName}.first_enddate` as any,
                                type: 'datepicker',
                                placeholder: t('first_enddate'),
                                disabled,
                                size: 'small',
                            },
                        },
                    ]}
                />

                {/* Continue Period */}
                {productGroupName !== 'DEVICE LIQUIDATION' && (
                    <>
                        <div className="border border-emerald-200/60 rounded-t-md">
                            <div className="px-3 py-2 bg-emerald-50/50 border-emerald-200/60">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        <span className="text-emerald-600 font-medium text-xs">
                                            2
                                        </span>
                                    </div>
                                    <Typogaphy className="font-medium text-emerald-800 text-sm">
                                        {t('services.continue_period')}
                                    </Typogaphy>
                                </div>
                            </div>
                        </div>
                        <GridHookForm
                            subForm
                            formControl={formControl}
                            actions={[]}
                            className="grid-cols-2 mt-2"
                            data={[
                                {
                                    formItemProps: {
                                        name: `${rootName}.continue_startdate` as any,
                                        type: 'datepicker',
                                        placeholder: t('continue_startdate'),
                                        disabled: true,
                                        size: 'small',
                                    },
                                    className:
                                        'bg-white rounded border border-slate-200/60',
                                },
                                {
                                    formItemProps: {
                                        name: `${rootName}.continue_enddate` as any,
                                        type: 'datepicker',
                                        placeholder: t('continue_enddate'),
                                        disabled,
                                        size: 'small',
                                    },
                                    className:
                                        'bg-white rounded border border-slate-200/60',
                                },
                            ]}
                        />
                    </>
                )}

                {/* Statement Configuration */}
                {!disabledStatement && !isAddon && (
                    <>
                        <div className="border border-orange-200/60 rounded-t-md">
                            <div className="px-3 py-2 bg-orange-50/50 border-orange-200/60">
                                <div className="flex items-center gap-2.5">
                                    <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        <StatementIcons className="w-3 h-3 text-orange-600" />
                                    </div>
                                    <Typogaphy className="font-medium text-orange-800 text-sm">
                                        {t('service-time-statement')}
                                    </Typogaphy>
                                </div>
                            </div>
                        </div>
                        <GridHookForm
                            subForm
                            className="mt-2"
                            formControl={formControl}
                            actions={[]}
                            data={[
                                {
                                    formItemProps: {
                                        name: `${rootName}.statement` as any,
                                        type: 'select',
                                        placeholder: t(
                                            'select_statement_period'
                                        ),
                                        allowClear: false,
                                        options: [
                                            {
                                                label: t('beginning_of_month'),
                                                value: 'beginning_of_month',
                                            },
                                            {
                                                label: t('day_23_to_22'),
                                                value: 'day_23_to_22',
                                            },
                                        ],
                                        size: 'small',
                                    },
                                    className:
                                        'col-span-4 bg-white rounded border border-slate-200/60',
                                },
                            ]}
                        />
                    </>
                )}
            </div>
        </div>
    )
}

export default ServiceTimes
